import { BaseText } from '@/src/components/BaseText'
import { BaseButton } from '@/src/components/buttons/BaseButton'
import { CardHeader } from '@/src/components/shared/card/CardHeader'
import { useLocationsContext } from '@/src/contexts/LocationsContext'
import { GranularityEnum } from '@/src/enums/granularity.enum'
import { LoadPlanningAdjustmentCounter } from '@/src/features/procurement-and-markets/load-planning/LoadPlanningAdjustmentCounter'
import { LoadPlanningRightSection } from '@/src/features/procurement-and-markets/load-planning/LoadPlanningRightSection'
import { LoadPlanningStatusIndicator } from '@/src/features/procurement-and-markets/load-planning/LoadPlanningStatusIndicator'
import { SaveLoadPlanningModal } from '@/src/features/procurement-and-markets/load-planning/SaveModal/LoadPlanningSave.modal'
import { createLoadPlanningSaveToastOptions } from '@/src/features/procurement-and-markets/load-planning/components/LoadPlanningSaveToast'
import {
  useLoadPlanningCurve,
  useLoadPlanningEarliestConsumption,
  useLoadedStrategyMetadata,
} from '@/src/features/procurement-and-markets/load-planning/hooks/LoadPlanning.hooks'
import { getCenterContentAreaHeight } from '@/src/shared/utils'
import { appColors } from '@/src/theme/foundations/colors'
import { Box, Flex, useDisclosure } from '@chakra-ui/react'

import { DataCompletenessIndicator } from '@/src/components/DataCompletenessIndicator/DataCompletenessIndicator'
import { useLoadPlanningDataCompleteness } from '@/src/features/procurement-and-markets/load-planning/hooks/useLoadPlanningDataCompleteness.hook'
import { isLoadPlanningDraft } from '@/src/features/procurement-and-markets/load-planning/load-planning-graph/LoadPlanning.utils'
import { useBaseToast } from '@/src/hooks/useBaseToast'
import { PROCUREMENT_AND_MARKETS_BASE_ROUTE, PROCUREMENT_STRATEGY_TYPES, TABS } from '@/src/utils/constants'
import { FlexCol, FlexRow } from '@components'
import {
  getReferencePeriodDatesFromReferenceDate,
  getReferencePeriodLabel,
  getUpToDateLast365PeriodDates,
  getUpToDateLast365PeriodLabel,
  isOutdatedLast365ReferenceDate,
} from '@features/procurement-and-markets/load-planning/ReferenceTimePeriod/ReferenceTimePeriod.utils'
import LoadPlanningAdjustmentModal from '@features/procurement-and-markets/load-planning/load-planning-adjustments/LoadPlanningAdjustmentModal'
import { format } from 'date-fns'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuFileSpreadsheet, LuGalleryHorizontal, LuRefreshCw } from 'react-icons/lu'
import { RxPlusCircled } from 'react-icons/rx'
import { useNavigate, useParams } from 'react-router-dom'
import { mapToEnergyTypeDto } from '../details/types/adjustment.utils'
import { useLoadPlanning } from '../hooks/useLoadPlanningsV2.hook'
import { ReferenceTimePeriodModal } from './ReferenceTimePeriod/ReferenceTimePeriodModal'
import { useRequestLoadProfile } from './hooks/useRequestLoadProfile.hooks'
import { getMinLatestReferenceDateTime } from './load-planning-adjustments/ReferencePeriodAdjustment.utils'

export const LoadPlanningPage = () => {
  const { t } = useTranslation()
  const toast = useBaseToast()
  const adjustmentModal = useDisclosure()
  const saveModal = useDisclosure()
  const navigate = useNavigate()
  const { setId } = useParams<{ setId?: string }>()
  const { companyPgId: companyId } = useLocationsContext()
  const referenceTimePeriodModal = useDisclosure()
  const [referenceTimePeriodOpenMode, setReferenceTimePeriodOpenMode] = useState<'reference' | 'refresh' | null>(null)
  const { mutate: requestLoadProfileMutation } = useRequestLoadProfile()

  const { data: strategyMetadata } = useLoadedStrategyMetadata()

  const energyType = mapToEnergyTypeDto(strategyMetadata?.energyType)

  const { data: loadPlanning, isFetched: isLoadPlanningFetched } = useLoadPlanning(companyId, strategyMetadata?.year, energyType)
  const loadPlanningMetaData = loadPlanning ? { attachedStrategyId: loadPlanning.attachedStrategyId } : undefined
  const attachedStrategyId = loadPlanningMetaData?.attachedStrategyId
  const {
    data: loadForecastData,
    isFetched: isLoadForecastFetched,
    isLoading: isLoadForecastLoading,
  } = useLoadPlanningCurve({
    companyId,
    locationId: undefined,
    year: strategyMetadata?.year,
    energyType,
    granularity: GranularityEnum.ONE_MONTH,
  })

  const { data: earliestConsumptions, isFetched: isEarliestConsumptionsFetched } = useLoadPlanningEarliestConsumption({
    companyId: companyId!,
    energyType,
    year: strategyMetadata?.year,
  })

  const includedLocationIds = useMemo(() => {
    if (!earliestConsumptions) return []
    return earliestConsumptions.map(entry => Number(entry.locationId))
  }, [loadForecastData?.includedLocationIds, earliestConsumptions])

  const latestReferenceDateTime = useMemo(() => getMinLatestReferenceDateTime(earliestConsumptions), [earliestConsumptions])

  const referencePeriodDates = useMemo(() => {
    if (!isLoadForecastFetched) return null

    if (loadForecastData?.referenceDateTime) {
      return (
        getReferencePeriodDatesFromReferenceDate(loadForecastData.referenceDateTime) ??
        getUpToDateLast365PeriodDates(latestReferenceDateTime)
      )
    }

    return getUpToDateLast365PeriodDates(latestReferenceDateTime)
  }, [isLoadForecastFetched, loadForecastData?.referenceDateTime, latestReferenceDateTime])

  const {
    items: completenessItems,
    scope: completenessScope,
    qualityPercentage,
    timeframe: completenessTimeframe,
  } = useLoadPlanningDataCompleteness({
    locationIds: includedLocationIds,
    energyType,
    startDate: referencePeriodDates ? format(referencePeriodDates.startDate, 'yyyy-MM-dd') : '',
    endDate: referencePeriodDates ? format(referencePeriodDates.endDate, 'yyyy-MM-dd') : '',
  })

  useEffect(() => {
    if (!setId && attachedStrategyId) {
      navigate(
        `${PROCUREMENT_AND_MARKETS_BASE_ROUTE}/${TABS.procurementAndMarkets.procurement}/${attachedStrategyId}/${TABS.procurement.mengenplanung}?type=${PROCUREMENT_STRATEGY_TYPES.HEDGED}&t=${TABS.forecasting.verbrauch}`,
        { replace: true }
      )
    }
  }, [attachedStrategyId, navigate, setId])

  if (!loadPlanningMetaData) {
    return isLoadPlanningFetched && isEarliestConsumptionsFetched ? (
      <ReferenceTimePeriodModal
        isOpen={true}
        updatingReferenceDateForFirstTime={true}
        showCustomBaseLoad={true}
        onClose={() => {
          navigate(`${PROCUREMENT_AND_MARKETS_BASE_ROUTE}/${TABS.procurementAndMarkets.procurement}`)
        }}
      />
    ) : null
  }

  const shouldShowRefreshReferencePeriodButton =
    !loadPlanning?.hasCustomReferenceBaseLoad &&
    isOutdatedLast365ReferenceDate(loadForecastData?.referenceDateTime, latestReferenceDateTime)

  const referenceDateRangeLabel = isLoadForecastFetched
    ? loadForecastData?.referenceDateTime
      ? getReferencePeriodLabel(loadForecastData?.referenceDateTime)
      : getUpToDateLast365PeriodLabel(latestReferenceDateTime)
    : '-'

  const isDraft = loadForecastData ? isLoadPlanningDraft(loadForecastData) : false

  const onSave = (consumptionDifference: number) => {
    if (attachedStrategyId) {
      toast(
        createLoadPlanningSaveToastOptions({
          consumptionDifference,
          onNavigate: () => {
            navigate(
              `${PROCUREMENT_AND_MARKETS_BASE_ROUTE}/${TABS.procurementAndMarkets.procurement}/${attachedStrategyId}/${TABS.procurement.portfolio}?type=${PROCUREMENT_STRATEGY_TYPES.HEDGED}&t=${TABS.portfolio.diagram}`
            )
          },
        })
      )
      return
    }

    toast({
      title: t('components.procurement.forecasting.save_modal.success'),
      status: 'success',
    })
  }

  const onRequestLoadProfile = () => {
    if (companyId && strategyMetadata?.year && strategyMetadata?.energyType) {
      requestLoadProfileMutation({
        requestBody: { companyId, year: strategyMetadata?.year.toString(), energyType: mapToEnergyTypeDto(strategyMetadata?.energyType) },
      })
      toast({
        title: t('components.procurement.forecasting.request_load_profile_success_toast'),
        status: 'success',
      })
    }
  }

  return (
    <FlexRow gap={4} height='100%' width='100%'>
      <FlexCol flex={1} gap={4} height={getCenterContentAreaHeight({ modifier: '- 160px' })} justifyContent='space-between'>
        <FlexCol gap={4}>
          <LoadPlanningStatusIndicator loadPlanningData={loadForecastData} />
          <FlexCol gap={4} borderTop={'1px solid'} borderColor={'border'} py='8px'>
            <CardHeader textKey={'components.procurement.forecasting.settings_header'} />
            <FlexRow justifyContent={'space-between'} alignItems={'center'}>
              <BaseText>{t('components.procurement.forecasting.reference_period_label')}</BaseText>
              {!loadPlanning?.hasCustomReferenceBaseLoad ? (
                <DataCompletenessIndicator
                  items={completenessItems}
                  scope={completenessScope}
                  timeframe={completenessTimeframe}
                  qualityPercentage={qualityPercentage}
                  description={t('components.procurement.forecasting.data_completeness_description')}
                />
              ) : null}
            </FlexRow>

            <FlexRow gap={2} align='center' width='100%'>
              <BaseButton
                flex='1'
                width='100%'
                variant='secondary'
                isDisabled={isLoadForecastLoading}
                onClick={() => {
                  setReferenceTimePeriodOpenMode('reference')
                  referenceTimePeriodModal.onOpen()
                }}
                data-action='loadplan-referencetime-trigger'
              >
                <span className='flex items-center gap-2'>
                  <LuGalleryHorizontal />
                  {loadPlanning?.hasCustomReferenceBaseLoad
                    ? t('components.procurement.forecasting.reference_period_modal.custom_base_load_label')
                    : referenceDateRangeLabel}
                </span>
              </BaseButton>
              {shouldShowRefreshReferencePeriodButton && (
                <BaseButton
                  variant='secondary'
                  aria-label={t('components.procurement.forecasting.refresh_reference_period_label')}
                  isDisabled={isLoadForecastLoading}
                  onClick={() => {
                    setReferenceTimePeriodOpenMode('refresh')
                    referenceTimePeriodModal.onOpen()
                  }}
                  data-action='loadplan-referencetime-reset-action'
                >
                  <LuRefreshCw color={appColors.amber['600']} />
                </BaseButton>
              )}
            </FlexRow>
          </FlexCol>
          <FlexCol gap={4} width='100%'>
            <BaseText>{t('components.procurement.forecasting.adjustments')}</BaseText>
            <FlexCol gap={2}>
              <BaseButton
                variant={isDraft ? 'secondary' : 'primary'}
                onClick={adjustmentModal.onOpen}
                isDisabled={isLoadForecastLoading}
                data-action='loadplan-newadjustment-trigger'
              >
                <span className='flex items-center gap-2'>
                  <RxPlusCircled />
                  {t('components.procurement.forecasting.create_button_text')}
                </span>
              </BaseButton>
              <LoadPlanningAdjustmentCounter />
            </FlexCol>
          </FlexCol>
        </FlexCol>

        <FlexCol borderTop={'1px solid'} borderColor={'border'} pt='16px' gap={2}>
          <BaseButton
            onClick={onRequestLoadProfile}
            width='100%'
            variant='tertiary'
            tooltip={t('components.procurement.forecasting.request_load_profile_button_tooltip')}
            data-action='loadplan-request-action'
          >
            <Flex gap={2} alignItems={'center'}>
              <LuFileSpreadsheet size={16} />
              <BaseText>{t('components.procurement.forecasting.request_load_profile_button')}</BaseText>
            </Flex>
          </BaseButton>
          <BaseButton
            width='100%'
            onClick={saveModal.onOpen}
            isDisabled={isLoadForecastLoading || !isDraft}
            variant={isDraft ? 'primary' : 'secondary'}
            data-action='loadplan-saveloadplan-trigger'
          >
            {t('components.procurement.forecasting.save_planning_button')}
          </BaseButton>
          <BaseText fontSize={'sm'} fontWeight={'var-reg'} mt='4px' textAlign={'center'}>
            {t('components.procurement.forecasting.save_planning_info_text')}
          </BaseText>
        </FlexCol>
      </FlexCol>

      <Box flex={3} borderLeft={'1px solid'} borderColor={'border'} px='12px'>
        <LoadPlanningRightSection />
      </Box>

      {adjustmentModal.isOpen && <LoadPlanningAdjustmentModal isOpen={adjustmentModal.isOpen} onClose={adjustmentModal.onClose} />}
      {saveModal.isOpen && <SaveLoadPlanningModal isOpen={saveModal.isOpen} onClose={saveModal.onClose} onSave={onSave} />}
      {referenceTimePeriodModal.isOpen && (
        <ReferenceTimePeriodModal
          isOpen={referenceTimePeriodModal.isOpen}
          onClose={() => {
            referenceTimePeriodModal.onClose()
            setReferenceTimePeriodOpenMode(null)
          }}
          referenceDate={loadForecastData?.referenceDateTime}
          isRefreshingLast365={referenceTimePeriodOpenMode === 'refresh'}
          showCustomBaseLoad
          hasStoredCustomBaseLoad={loadPlanning?.hasCustomReferenceBaseLoad}
        />
      )}
    </FlexRow>
  )
}
